import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Printer, CheckCircle2, ShoppingCart, HelpCircle } from "lucide-react";
import { pluralUnit } from "./unitLabel";
import { formatMDY } from "./dateFormat";
import { Button } from "@/components/ui/button";
import PrintReportHeader from "@/components/common/PrintReportHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MenuTypeBadges = ({ itemId, types }) => {
  if (!types || types.length === 0) return null;
  return (
    <span className="inline-flex gap-1 ml-2" data-testid={`shopping-list-badges-${itemId}`}>
      {types.includes("lunch") && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" data-testid={`badge-lunch-${itemId}`}>L</span>
      )}
      {types.includes("breakfast_snack") && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid={`badge-breakfast-snack-${itemId}`}>B/S</span>
      )}
    </span>
  );
};

const STATUS_META = {
  needs_to_buy: { label: "Needs to Buy", className: "bg-rose-100 text-rose-700", Icon: ShoppingCart },
  fully_stocked: { label: "Fully Stocked", className: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  missing_info: { label: "Missing Info", className: "bg-amber-100 text-amber-700", Icon: HelpCircle },
};

const StatusBadge = ({ itemId, status, reason }) => {
  const meta = STATUS_META[status] || STATUS_META.missing_info;
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.className}`}
      data-testid={`shopping-list-status-${itemId}`}
      title={reason || undefined}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

// Stage 3: shows every item confirmed this month, not just what needs buying — "missing info"
// (no yield, no Times Served, not yet counted) stays visibly flagged, never shown as Fully Stocked.
export const ShoppingListPanel = ({ month, year, authHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({});

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/inventory/shopping-list`, { params: { month, year }, headers: authHeaders });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load shopping list");
    } finally {
      setLoading(false);
    }
  }, [month, year, authHeaders]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const toggleChecked = (itemId) => setChecked((c) => ({ ...c, [itemId]: !c[itemId] }));

  if (loading) {
    return <p className="text-sm text-slate-400 py-6 text-center">Loading shopping list...</p>;
  }

  if (error) {
    return (
      <p data-testid="shopping-list-not-ready" className="text-sm text-slate-500 py-6 text-center">
        {error}
      </p>
    );
  }

  const items = data?.items || [];
  const buyCount = items.filter((i) => i.status === "needs_to_buy").length;

  return (
    <div data-testid="shopping-list-panel" className="space-y-4">
      <PrintReportHeader reportTitle="Shopping List" />
      <div className="flex items-center justify-between print:hidden">
        <p className="text-sm text-slate-500">
          Approved by {data.approvedBy} on {formatMDY(data.approvedAt)} — {buyCount} item{buyCount !== 1 ? "s" : ""} to buy.
        </p>
        <Button size="sm" variant="outline" data-testid="print-shopping-list-button" onClick={() => window.print()}>
          <Printer className="w-4 h-4 mr-1" /> Print
        </Button>
      </div>
      {items.length > 0 && (
        <p className="text-xs text-slate-500" data-testid="shopping-list-badge-legend">
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 mr-1">L</span> Lunch
          <span className="mx-2">·</span>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 mr-1">B/S</span> Breakfast/Snack
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center">No items were confirmed for this month yet.</p>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 print:hidden">Got it</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Quantity to Buy</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                // Skip dishes that just repeat the item's own name (a plain 1:1 match) — only
                // worth showing when it tells you something new, e.g. a shared/split ingredient.
                const relevantDishes = (item.sourceDishes || []).filter(
                  (d) => d.toLowerCase() !== item.canonicalName.toLowerCase()
                );
                return (
                <tr key={item.itemId} data-testid={`shopping-list-row-${item.itemId}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 print:hidden">
                    {item.status === "needs_to_buy" && (
                      <input type="checkbox" data-testid={`shopping-list-check-${item.itemId}`}
                        checked={!!checked[item.itemId]} onChange={() => toggleChecked(item.itemId)} />
                    )}
                  </td>
                  <td className={`px-3 py-2 font-medium ${checked[item.itemId] ? "line-through text-slate-400" : "text-slate-800"}`}>
                    {item.canonicalName}
                    <MenuTypeBadges itemId={item.itemId} types={item.sourceMenuTypes} />
                    {relevantDishes.length > 0 && (
                      <p className="text-xs font-normal text-slate-400 mt-0.5" data-testid={`shopping-list-used-in-${item.itemId}`}>
                        Used in: {relevantDishes.join(", ")}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge itemId={item.itemId} status={item.status} reason={item.statusReason} />
                    {item.statusReason && (
                      <p className="text-[11px] text-slate-400 mt-0.5 print:hidden">{item.statusReason}</p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {item.quantityToBuy != null ? `${item.quantityToBuy} ${pluralUnit(item.unitType, item.quantityToBuy)}` : "—"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ShoppingListPanel;
